import { ReactElement, ReactNode } from "react";

import { DialogProps as MuiDialogProps } from "@mui/material/Dialog";
import { ClassNameMap } from "@mui/styles";
import { Emptyable } from "@simplyk/common";

import { DashboardButtonProps } from "../Button";
import { InternalClasses } from "../helpers";
import { DashboardTooltipProps } from "../Tooltip";

export type BaseDialogClasses =
  | "globalPadding"
  | "root"
  | "paper"
  | "paperFullWidth"
  | "container"
  | "header"
  | "iconCloseContainer"
  | "iconWrapper"
  | "subtitle"
  | "icon"
  | "defaultColor"
  | "content"
  | "footer";

const PREFIX = "ZeffyDialog";

export const zeffyDialogClasses: InternalClasses<typeof PREFIX, BaseDialogClasses> = {
  globalPadding: `${PREFIX}-globalPadding`,
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  container: `${PREFIX}-container`,
  paperFullWidth: `${PREFIX}-paperFullWidth`,
  header: `${PREFIX}-header`,
  iconCloseContainer: `${PREFIX}-iconCloseContainer`,
  iconWrapper: `${PREFIX}-iconWrapper`,
  subtitle: `${PREFIX}-subtitle`,
  icon: `${PREFIX}-icon`,
  defaultColor: `${PREFIX}-defaultColor`,
  content: `${PREFIX}-content`,
  footer: `${PREFIX}-footer`,
};

export type ActionProps = Partial<DashboardButtonProps> &
  ({ onClick: DashboardButtonProps["onClick"] } | { href: string } | { hidden: true }) & {
    tooltip?: { title: string; placement?: DashboardTooltipProps["placement"] };
  };
export type AdditionalActionProps = ActionProps & {
  children: ReactNode | string;
  onClick: DashboardButtonProps["onClick"];
};

export const dialogVibeOptions = ["brand", "danger", "warning", "positive"] as const;
export type DialogVibe = (typeof dialogVibeOptions)[number];

export type DialogProps = Omit<MuiDialogProps, "classes" | "onClose" | "title" | "slots" | "slotProps"> & {
  actions?: {
    main?: ActionProps;
    cancel?: ActionProps;
    additional?: AdditionalActionProps[];
  };
  classes?: Partial<ClassNameMap<BaseDialogClasses>>;
  icon?: ReactElement<unknown>;
  renderIcon?: Emptyable<() => ReactElement<unknown>>;
  fullScreenOnMobile?: boolean;
  title?: ReactElement<unknown> | string;
  vibe?: DialogVibe;
  onClose?: () => void;
  dialogContentId?: string;
  hideCloseButton?: boolean;
  hideIcon?: boolean;
  slots?: {
    title?: ReactElement<unknown>;
  };
};

export const DEFAULT_DIALOG_VIBE = "brand";
export const DEFAULT_DIALOG_MAXWIDTH = "sm";

export type DialogSsrProps = DialogProps & {
  forwardedRef?: React.Ref<HTMLDivElement>;
};
